import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Loader2, Star, CalendarDays, User, MessageSquare } from "lucide-react";

export default function CoachRatings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/coach/ratings").then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;
  if (!data) return <div className="text-center py-20 font-heading text-xl">Unable to load ratings</div>;

  const renderStars = (score) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(s => (
        <Star key={s} className={`w-4 h-4 ${s <= score ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
      ))}
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-6 md:py-10" data-testid="coach-ratings-page">
      <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight flex items-center gap-3 mb-2">
        <Star className="w-8 h-8 text-amber-400" />My Ratings
      </h1>
      <p className="text-muted-foreground mb-8">Feedback from your session participants</p>

      {/* Summary */}
      <div className="bg-white border-2 border-slate-100 p-6 mb-8" data-testid="rating-summary">
        <div className="flex items-center gap-8 flex-wrap">
          <div className="text-center">
            <p className="font-heading text-5xl font-bold text-court-orange">{data.average_rating > 0 ? data.average_rating.toFixed(1) : "—"}</p>
            <div className="flex gap-0.5 justify-center mt-1">
              {[1, 2, 3, 4, 5].map(s => (
                <Star key={s} className={`w-5 h-5 ${s <= Math.round(data.average_rating) ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
              ))}
            </div>
            <p className="text-sm text-muted-foreground mt-1">{data.total_ratings} rating{data.total_ratings !== 1 ? 's' : ''}</p>
          </div>
          <Separator orientation="vertical" className="h-20 hidden md:block" />
          <div className="flex-1 space-y-1.5">
            {data.distribution?.slice().reverse().map(d => {
              const pct = data.total_ratings > 0 ? (d.count / data.total_ratings) * 100 : 0;
              return (
                <div key={d.stars} className="flex items-center gap-2 text-sm">
                  <span className="w-8 text-right font-heading font-bold">{d.stars}</span>
                  <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                  <div className="flex-1 h-2.5 bg-slate-100 overflow-hidden">
                    <div className="h-full bg-amber-400 transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-8 text-xs text-muted-foreground">{d.count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Reviews */}
      <h2 className="font-heading text-xl font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
        <MessageSquare className="w-5 h-5 text-court-orange" />Reviews
      </h2>
      {data.reviews?.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-slate-200">
          <Star className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="font-heading text-lg text-muted-foreground uppercase">No reviews yet</p>
          <p className="text-sm text-muted-foreground mt-1">Complete sessions and your participants can rate you</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.reviews.map(r => (
            <div key={r.rating_id} className="bg-white border-2 border-slate-100 p-4" data-testid={`review-${r.rating_id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium text-sm">{r.rater_name}</span>
                  {renderStars(r.score)}
                </div>
                <span className="text-xs text-muted-foreground">{r.created_at?.slice(0, 10)}</span>
              </div>
              {r.session_title && (
                <p className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
                  <CalendarDays className="w-3 h-3" />{r.session_title} - {r.session_date}
                </p>
              )}
              {r.comment && <p className="text-sm text-muted-foreground">{r.comment}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
